import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Minus, User, 
  Users, ChevronRight, RefreshCcw, Info, Eye, Star, Trophy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Buyer Seriousness Scoring (BSS) Admin Panel
 * Phase 1: Shadow Mode - Internal Admin Only
 */
const BSSAdminPanel = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [buyerDetails, setBuyerDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOverview();
  }, [token]);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/bss/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setOverview(data.data);
      } else {
        setError('Failed to load BSS overview');
      }
    } catch (err) {
      setError('Error connecting to BSS service');
    }
    setLoading(false);
  };

  const fetchBuyerDetails = async (buyerId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/bss/buyers/${buyerId}/score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setBuyerDetails(data.data);
        setSelectedBuyer(buyerId);
      }
    } catch (err) {
      console.error('Error fetching buyer details:', err);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'VERIFIED_PRIME': return 'bg-purple-600/20 text-purple-400 border-purple-600/30';
      case 'SERIOUS': return 'bg-green-600/20 text-green-400 border-green-600/30';
      case 'EXPLORATORY': return 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30';
      case 'NON_PRIORITY': return 'bg-red-600/20 text-red-400 border-red-600/30';
      default: return 'bg-gray-600/20 text-gray-400 border-gray-600/30';
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'VERIFIED_PRIME': return 'Verified Prime';
      case 'SERIOUS': return 'Serious';
      case 'EXPLORATORY': return 'Exploratory';
      case 'NON_PRIORITY': return 'Non-Priority';
      default: return 'Unscored';
    }
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'UP': return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'DOWN': return <TrendingDown className="w-4 h-4 text-red-400" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'HIGH': return 'text-green-400';
      case 'MEDIUM': return 'text-yellow-400';
      case 'LOW': return 'text-orange-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="p-8 text-center">
          <RefreshCcw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading Buyer Seriousness Scores...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass-card border-red-600/30">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-red-400" />
          <p className="text-red-400">{error}</p>
          <Button onClick={fetchOverview} className="mt-4">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display text-primary">Buyer Seriousness Scoring</h2>
          <p className="text-sm text-muted-foreground">Phase 1: Shadow Mode - Internal Only</p>
        </div>
        <Button variant="outline" onClick={fetchOverview} className="gap-2">
          <RefreshCcw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Phase Banner */}
      <Card className="glass-card border-purple-600/30 bg-purple-600/5">
        <CardContent className="p-4 flex items-center gap-4">
          <Info className="w-5 h-5 text-purple-400" />
          <div>
            <p className="text-purple-400 font-medium">Shadow Mode Active</p>
            <p className="text-xs text-muted-foreground">
              BSS determines viewing priority and offer credibility weight. Currently in observation mode.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tier Distribution */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="glass-card border-purple-600/30">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Star className="w-4 h-4 text-purple-400" />
              <span className="text-3xl font-display text-purple-400">{overview?.verified_prime_count || 0}</span>
            </div>
            <p className="text-xs text-muted-foreground">Verified Prime</p>
            <p className="text-xs text-purple-400">85-100</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-green-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-green-400">{overview?.serious_count || 0}</div>
            <p className="text-xs text-muted-foreground">Serious</p>
            <p className="text-xs text-green-400">70-84</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-yellow-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-yellow-400">{overview?.exploratory_count || 0}</div>
            <p className="text-xs text-muted-foreground">Exploratory</p>
            <p className="text-xs text-yellow-400">50-69</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-red-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-red-400">{overview?.non_priority_count || 0}</div>
            <p className="text-xs text-muted-foreground">Non-Priority</p>
            <p className="text-xs text-red-400">{"<50"}</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-gray-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-gray-400">{overview?.unscored_count || 0}</div>
            <p className="text-xs text-muted-foreground">Unscored</p>
            <p className="text-xs text-gray-400">No Data</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Buyer List */}
        <Card className="glass-card border-white/10 col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Buyer Scores
              <Badge variant="secondary" className="ml-auto">{overview?.total_buyers || 0} Buyers</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {overview?.buyers?.map((buyer) => (
                <div
                  key={buyer.buyer_id}
                  onClick={() => fetchBuyerDetails(buyer.buyer_id)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedBuyer === buyer.buyer_id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`buyer-row-${buyer.buyer_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-secondary/30 flex items-center justify-center">
                        {buyer.score !== null ? (
                          <span className="text-lg font-display text-primary">{Math.round(buyer.score)}</span>
                        ) : (
                          <User className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{buyer.buyer_name || 'Unknown Buyer'}</p>
                        <p className="text-xs text-muted-foreground">{buyer.email}</p>
                        <div className="flex gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">
                            Credibility: <span className="text-primary">{buyer.credibility_multiplier?.toFixed(2)}x</span>
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Priority: <span className="text-primary">+{buyer.viewing_priority_boost?.toFixed(1)}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={getTierColor(buyer.tier)}>
                        {getTierLabel(buyer.tier)}
                      </Badge>
                      <div className="flex items-center gap-1">
                        {getTrendIcon(buyer.trend_direction)}
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                  {buyer.flags?.length > 0 && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      {buyer.flags.map((flag, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs border-orange-600/30 text-orange-400">
                          {flag.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {overview?.buyers?.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No buyers found</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Buyer Details Panel */}
        <Card className="glass-card border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              Score Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {buyerDetails ? (
              <div className="space-y-4">
                {/* Overall Score */}
                <div className="text-center p-4 rounded-lg bg-secondary/20">
                  <div className={`text-5xl font-display mb-2 ${
                    buyerDetails.score !== null ? 'text-primary' : 'text-muted-foreground'
                  }`}>
                    {buyerDetails.score !== null ? Math.round(buyerDetails.score) : '--'}
                  </div>
                  <Badge className={getTierColor(buyerDetails.tier)}>
                    {getTierLabel(buyerDetails.tier)}
                  </Badge>
                  <p className={`text-xs mt-2 ${getConfidenceColor(buyerDetails.confidence)}`}>
                    Confidence: {buyerDetails.confidence}
                  </p>
                </div>

                {/* Derived Metrics */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 rounded bg-purple-600/10 text-center">
                    <div className="text-lg font-display text-purple-400">
                      {buyerDetails.credibility_multiplier?.toFixed(2)}x
                    </div>
                    <p className="text-xs text-muted-foreground">Credibility</p>
                  </div>
                  <div className="p-3 rounded bg-blue-600/10 text-center">
                    <div className="text-lg font-display text-blue-400">
                      +{buyerDetails.viewing_priority_boost?.toFixed(1)}
                    </div>
                    <p className="text-xs text-muted-foreground">Priority Boost</p>
                  </div>
                </div>

                {/* Status */}
                <div className="p-3 rounded bg-secondary/10 text-sm">
                  <p className="text-muted-foreground">{buyerDetails.summary}</p>
                </div>

                {/* Category Breakdown */}
                {buyerDetails.categories?.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Category Breakdown</p>
                    {buyerDetails.categories.map((cat, idx) => (
                      <div key={idx} className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">
                            {cat.category.replace(/_/g, ' ')}
                          </span>
                          <span className={cat.signal_available ? 'text-foreground' : 'text-muted-foreground'}>
                            {cat.signal_available ? `${Math.round(cat.raw_score)}/100` : 'N/A'}
                          </span>
                        </div>
                        <Progress 
                          value={cat.signal_available ? cat.raw_score : 0} 
                          className="h-2"
                        />
                        <p className="text-xs text-muted-foreground">
                          Weight: {(cat.weight * 100).toFixed(0)}%
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Flags */}
                {buyerDetails.flags?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-orange-400">Flags</p>
                    {buyerDetails.flags.map((flag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs border-orange-600/30 text-orange-400 block text-center py-1">
                        {flag.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Improvement Suggestions */}
                {buyerDetails.improvement_suggestions?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-yellow-400">Improvement Areas</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {buyerDetails.improvement_suggestions.map((suggestion, idx) => (
                        <li key={idx}>• {suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Metadata */}
                <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t border-white/10">
                  <p>Interactions: {buyerDetails.total_interactions}</p>
                  <p>Events: {buyerDetails.total_events}</p>
                  <p>Calculated: {new Date(buyerDetails.calculated_at).toLocaleString()}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <User className="w-8 h-8 mx-auto mb-4 opacity-50" />
                <p>Select a buyer to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BSSAdminPanel;
